import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-white" data-testid="terms-of-service-page">
      <header className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
          <Link to="/jobs" className="flex items-center gap-2 text-blue-700 font-semibold hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to DentalStaff
          </Link>
          <p className="text-sm text-slate-500">Last updated: February 2026</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 text-slate-700 leading-relaxed [&_h1]:text-4xl [&_h1]:font-extrabold [&_h1]:text-slate-900 [&_h1]:mb-6 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mt-10 [&_h2]:mb-4 [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-slate-900 [&_h3]:mt-6 [&_h3]:mb-3 [&_p]:my-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-4 [&_li]:my-1.5 [&_a]:text-blue-700 [&_a]:underline hover:[&_a]:text-blue-900 [&_strong]:text-slate-900">
        <div className="not-prose mb-8 p-4 bg-amber-50 border-l-4 border-amber-400 rounded-r-lg text-sm text-amber-900">
          <strong>Note:</strong> This document was AI-drafted as a starting point and should be reviewed
          by qualified legal counsel before being treated as a final Terms of Service agreement. Last
          updated: February 2026.
        </div>

        <h1>Terms of Service</h1>
        <p>
          These Terms of Service ("Terms") govern your access to and use of DentalStaff by PractCom (the
          "Service"), operated by The Oncall Bot LLC ("The Oncall Bot", "PractCom", "we", "us", or "our"). By creating an
          account or otherwise using the Service, you agree to these Terms.
        </p>

        <h2>1. Acceptance of Terms</h2>
        <p>
          By creating an account or using any feature of the Service, you affirm that you have read,
          understood, and agree to be bound by these Terms and our Privacy Policy. If you do not agree,
          do not use the Service.
        </p>

        <h2>2. Eligibility</h2>
        <ul>
          <li>
            <strong>Job Seekers:</strong> You must be at least 18 years old and legally eligible to work
            in the United States or in the jurisdiction where you intend to seek employment. You must be
            seeking, considering, or actively employed in the dental industry.
          </li>
          <li>
            <strong>Employer/Practice users:</strong> You must be an authorized representative of a
            dental practice that holds an active paid subscription to PractCom. Use of the "Hire Staff"
            module is included with that paid subscription.
          </li>
          <li>
            By using the Service, you represent and warrant that you meet these eligibility requirements.
          </li>
        </ul>

        <h2>3. Job Seeker Obligations</h2>
        <p>If you use the Service as a job seeker, you agree to the following:</p>
        <ul>
          <li>Provide accurate, current, and truthful information in your profile and application
            materials.</li>
          <li>Submit only your own original resume and not the work of any other person.</li>
          <li>Maintain only one (1) job-seeker account; multiple accounts are prohibited.</li>
          <li>Not submit fraudulent, fake, or test applications to any posting.</li>
          <li>Not use the Service to harvest contact information for any purpose other than legitimately
            seeking employment.</li>
          <li>Keep your password confidential and notify us promptly of any unauthorized account access.</li>
          <li>Not impersonate any other person or misrepresent your identity, qualifications, or
            credentials.</li>
        </ul>

        <h2>4. Dental Practice (Employer) Obligations</h2>
        <p>If you use the Service as a dental practice posting a job, you agree to the following:</p>
        <ul>
          <li>Each job posting must represent a real, current, available opening at your practice.</li>
          <li>Job descriptions must be accurate and not misleading regarding compensation, role,
            location, schedule, or working conditions.</li>
          <li>You must comply with all applicable employment laws, including but not limited to Equal
            Employment Opportunity (EEO) laws, the Americans with Disabilities Act (ADA), the Fair Labor
            Standards Act (FLSA), and all applicable state and local employment regulations.</li>
          <li>You may use applicant data received through the Service exclusively for evaluating that
            applicant for the position to which they applied. You may not redistribute, sell, or
            repurpose this data for any other use.</li>
          <li>You may not contact applicants for any purpose unrelated to the specific position to which
            they applied without the applicant's express consent.</li>
          <li>You will keep applicant information confidential within your practice.</li>
        </ul>

        <h2>5. Platform Role — Limitation of Liability</h2>
        <p>
          <strong>PractCom is a technology platform that connects dental job seekers with dental
          practices. PractCom is not a staffing agency, recruiter, or employer.</strong> PractCom does
          not guarantee employment, the quality of any job posting, the qualifications of any applicant,
          the accuracy of any information posted by users, or the outcome of any hiring decision. All
          hiring, employment, compensation, and workplace decisions are solely between the job seeker and
          the dental practice.
        </p>
        <p>
          To the fullest extent permitted by law, PractCom is not liable for: (a) any acts or omissions
          of users of the Service, including without limitation any fraudulent job postings, fake
          applications, misrepresentations, or unlawful conduct by job seekers or practices; (b) any
          employment decisions made by practices using the Service; (c) any disputes between job seekers
          and practices; or (d) any indirect, incidental, special, consequential, or punitive damages
          arising out of or related to your use of the Service.
        </p>
        <p>
          To the fullest extent permitted by law, PractCom's total aggregate liability arising out of or
          related to these Terms or the Service shall not exceed one hundred dollars ($100) USD.
        </p>

        <h2>6. Application Consent</h2>
        <p>
          By submitting an application through the Service, you (the job seeker) explicitly consent to
          your resume, name, email address, phone number (if provided), and any cover note you authored
          being transmitted to and reviewed by the specific dental practice that posted that specific
          job. This consent is documented at the time of submission, including a timestamp and
          identification of which posting received your application.
        </p>

        <h2>7. Intellectual Property</h2>
        <p>
          You retain ownership of all content you submit to the Service, including your resume, profile
          information, and any communications. By uploading content, you grant PractCom a limited,
          worldwide, royalty-free, non-exclusive license to store, transmit, format, and display that
          content solely for the purpose of operating the Service (e.g., delivering your resume to
          practices to which you have applied).
        </p>
        <p>
          PractCom retains all rights, title, and interest in the Service itself, including all software,
          designs, trademarks, logos, and platform-generated content. You may not copy, modify, reverse
          engineer, or create derivative works of the Service without express written permission.
        </p>

        <h2>8. Prohibited Conduct</h2>
        <p>You may not, in connection with the Service:</p>
        <ul>
          <li>Use the Service for any unlawful purpose or to facilitate unlawful conduct.</li>
          <li>Attempt to gain unauthorized access to any account, server, or system associated with the
            Service.</li>
          <li>Interfere with, disrupt, or burden the Service or the servers and networks connected to
            the Service.</li>
          <li>Use automated systems (bots, scrapers, crawlers) to access the Service or collect content
            from the Service.</li>
          <li>Upload malicious software, viruses, or harmful code.</li>
          <li>Discriminate against any applicant in violation of applicable law.</li>
          <li>Post job listings for positions outside the dental industry.</li>
          <li>Use the Service to harass, abuse, threaten, or otherwise mistreat any other user.</li>
        </ul>

        <h2>9. Account Termination</h2>
        <p>
          You may delete your account at any time through your profile settings. Upon deletion, your data
          will be handled in accordance with the data retention practices described in our Privacy Policy.
        </p>
        <p>
          PractCom reserves the right to suspend or terminate any account, with or without notice, that
          violates these Terms or whose conduct PractCom determines, in its sole discretion, is harmful
          to other users, third parties, or the Service. PractCom is not liable to you or any third
          party for any termination of your account.
        </p>

        <h2>10. Disclaimers</h2>
        <p>
          The Service is provided "AS IS" and "AS AVAILABLE," without warranties of any kind, either
          express or implied, including warranties of merchantability, fitness for a particular purpose,
          non-infringement, or any warranty regarding the accuracy, completeness, or reliability of any
          information or content posted by users. PractCom does not warrant that the Service will be
          uninterrupted, error-free, or free of viruses or other harmful components.
        </p>

        <h2>11. Indemnification</h2>
        <p>
          You agree to indemnify, defend, and hold harmless PractCom and its officers, directors,
          employees, and agents from and against all claims, liabilities, damages, losses, and expenses
          (including reasonable attorneys' fees) arising out of or related to: (a) your use of the
          Service; (b) your violation of these Terms; (c) your violation of any third party's rights,
          including any privacy or intellectual-property right; or (d) any content you submit or
          transmit through the Service.
        </p>

        <h2>12. Governing Law and Dispute Resolution</h2>
        <p>
          These Terms are governed by the laws of the State of New York, United States, without regard to its conflict-of-law principles. Any dispute
          arising out of or related to these Terms or the Service shall be resolved exclusively in the
          state or federal courts located in Suffolk County, New York, and you consent to the personal
          jurisdiction of those courts.
        </p>

        <h2>13. Changes to These Terms</h2>
        <p>
          PractCom may modify these Terms at any time. We will notify you of material changes by email
          and by posting a notice on the Service at least 30 days before the changes take effect. Your
          continued use of the Service after the effective date of the revised Terms constitutes your
          acceptance of the revised Terms.
        </p>

        <h2>14. Entire Agreement</h2>
        <p>
          These Terms, together with the Privacy Policy and any additional terms presented within the
          Service, constitute the entire agreement between you and PractCom regarding the Service and
          supersede any prior agreements.
        </p>

        <h2>15. Severability</h2>
        <p>
          If any provision of these Terms is held to be invalid or unenforceable by a court of competent
          jurisdiction, the remaining provisions will remain in full force and effect.
        </p>

        <h2>16. Contact</h2>
        <p>
          For questions about these Terms, please contact:<br />
          <strong>The Oncall Bot LLC</strong><br />
          Email: <a href="mailto:admin@practcom.com">admin@practcom.com</a>
        </p>
      </main>

      <footer className="border-t border-slate-200 bg-slate-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center text-sm text-slate-500">
          <Link to="/jobs" className="text-blue-700 hover:underline font-semibold">
            DentalStaff by PractCom
          </Link>
          {' · '}
          <Link to="/privacy" className="hover:text-slate-700">Privacy Policy</Link>
          {' · '}© {new Date().getFullYear()} The Oncall Bot LLC.
        </div>
      </footer>
    </div>
  );
}
